import React, { useEffect, useState, useRef, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  where,
} from 'firebase/firestore'
import { db } from '../../firebase'

import PaginatedTable from '../PaginatedTable'
import Spinner from '../Spinner'
import { ApiContext, useApiRequest } from '../../hooks/api'

export default function SitesList() {
  const navigate = useNavigate()
  const { user } = useContext(ApiContext)
  const [profile] = useApiRequest(`users/${user?.id}`)

  const [rows, setRows] = useState([])
  const [currentPage, setCurrentPage] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [loading, setLoading] = useState(true)

  const [opts, setOpts] = useState({
    itemsPerPage: 10,
    sortBy: 'name',
    sortOrder: 'asc',
    persistent: true,
    uid: 'sites-list',
  })

  const pageCursors = useRef({})

  // your org id (from user profile)
  const org_id = Number(profile?.organization?.mapValue?.fields?.id?.integerValue)

  // organization_bindings holds org ids as strings (mobile writes orgId.toString(),
  // SiteDetails removes with String(organizationId)), so the query key is the string form.
  const org_key = String(org_id)

  const columns = [
    { name: 'ID', sortKey: 'id', render: (item) => item.id },
    { name: 'Name', sortKey: 'name', render: (item) => item.name },
    {
      name: 'Organization',
      render: (item) => item.organization?.org_name || '—',
    },
  ]

  useEffect(() => {
    if (!org_id) return

    const fetchSites = async () => {
      setLoading(true)
      try {
        const sitesRef = collection(db, 'sites')

        let q = query(
          sitesRef,
          where('organization_bindings', 'array-contains', org_key),
          orderBy(opts.sortBy, opts.sortOrder),
          limit(opts.itemsPerPage)
        )

        if (currentPage > 0 && pageCursors.current[currentPage - 1]) {
          q = query(
            sitesRef,
            where('organization_bindings', 'array-contains', org_key),
            orderBy(opts.sortBy, opts.sortOrder),
            startAfter(pageCursors.current[currentPage - 1]),
            limit(opts.itemsPerPage)
          )
        }

        const snapshot = await getDocs(q)
        const siteData = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))
        setRows(siteData)

        // save lastDoc cursor for this page
        if (snapshot.docs.length > 0) {
          pageCursors.current[currentPage] =
            snapshot.docs[snapshot.docs.length - 1]
        }

        // fetch total count only once
        if (totalCount === 0) {
          const countSnap = await getCountFromServer(
            query(sitesRef, where('organization_bindings', 'array-contains', org_key))
          )
          setTotalCount(countSnap.data().count)
        }
      } catch (err) {
        console.error('Error fetching sites:', err)
      } finally {
        setLoading(false)
      }
    }

    fetchSites()
  }, [currentPage, opts, org_id])

  const setPage = (page) => setCurrentPage(page)

  const setOptions = (newOpts) => {
    setOpts((prev) => ({ ...prev, ...newOpts }))
    setCurrentPage(0) // reset when sort/filter changes
  }

  if (loading) return <Spinner />

  return (
    <div className="box">
      <PaginatedTable
        className="is-fullwidth is-hoverable"
        columns={columns}
        rows={rows}
        currentPage={currentPage}
        totalCount={totalCount}
        loading={loading}
        setPage={setPage}
        opts={opts}
        setOpts={setOptions}
        onRowClick={(item) => navigate(`/sites/${item.id}`)}
      />
    </div>
  )
}
